import Phaser from 'phaser'
import Config from '../launch/config'
import Entity from '../entity/entity'
import RadialGradientArc from '../radial-gradient-arc/radial-gradient-arc'
import tinycolor from 'tinycolor2'
import {isDebug} from '../launch/utils'

export default class Planet extends Entity {
    constructor(props = {}) {
        const chance = Config.chance
        const climate =
            props.climate ||
            chance.pick(['warm', 'cold', 'moderate', 'gas', 'ice', 'hot'])
        const climateMap = {
            cold: {
                hue: chance.integer({min: 150, max: 240}),
            },
            gas: {
                hue: chance.integer({min: 0, max: 330}),
            },
            hot: {
                hue: chance.integer({min: 0, max: 40}),
            },
            ice: {
                hue: chance.integer({min: 210, max: 250}),
            },
            moderate: {
                hue: chance.integer({min: 90, max: 150}),
            },
            warm: {
                hue: chance.integer({min: 40, max: 60}),
            },
        }

        const fillColor = tinycolor(
            'hsl(' + climateMap[climate].hue + ', 60%, 50%)'
        )
            .brighten(10)
            .toHex()

        props.fillColor = props.fillColor || `0x${fillColor}`
        props.diameter =
            props.diameter ||
            Phaser.Math.Between(Config.planetMinSize, Config.planetMaxSize)
        props.type = 'planet'

        super(props)

        const fillColorHex = props.fillColor.replace('0x', '')
        const fillColorLight = tinycolor(fillColorHex)
            .brighten(30)
            .toString()
        this.fillColorLight = fillColorLight.replace('#', '0x')
        const fillColorDark = tinycolor(fillColorHex)
            .darken(20)
            .toString()
        this.fillColorDark = fillColorDark.replace('#', '0x')

        this.colonized = props.colonized || false
        this.discovered =
            props.discovered ||
            (props.colonized && props.faction === Config.userFactionName) ||
            false
        this.isBuildingShip = false
        this.isBuildingSatellite = false
        this.isBuildingMine = false
        this.isColonizing = false
        this.climate = climate
        // this.population = chance.integer({min: 0, max: 100000})
        // this.minerals = chance.integer({min: 0, max: 10000})
        // this.water =
        //     climateMap[climate].water || chance.integer({min: 0, max: 10000})

        const iconRowY = this.y - this.diameter - 8
        const iconDepth = 3

        this.enemyFlagTextureMap = {
            0: 'flagRed',
            1: 'flagOrange',
            2: 'flagBlue',
            3: 'flagGreen',
        }

        // Flag
        this.flag = Config.scene.add.sprite(
            this.x,
            iconRowY,
            this.getFlagTexture()
        )
        this.flag.setScale(0.17)
        this.flag.setDepth(iconDepth)
        this.flag[Config.namespace] = {type: 'flag'}

        // Home icon
        if (props.isHomePlanet) {
            this.homeSprite = Config.scene.add.sprite(
                this.x - 20,
                iconRowY,
                'homeGreen'
            )
            this.homeSprite.setScale(0.15)
            this.homeSprite.setDepth(iconDepth)
            this.homeSprite[Config.namespace] = {type: 'homeSprite'}
        }

        // Resources icon
        this.resourcesSprite = Config.scene.add.sprite(
            this.x + 20,
            iconRowY,
            'resourcesGreen'
        )
        this.resourcesSprite.setScale(0.22)
        this.resourcesSprite.setDepth(iconDepth)
        this.resourcesSprite[Config.namespace] = {type: 'resourcesSprite'}

        // Gradient
        this.gradientArc = new RadialGradientArc({
            diameter: this.diameter,
            innerColor: fillColorLight,
            middleColor: fillColorHex,
            outerColor: fillColorDark,
            textureName: `planet-${this.id}`,
            x: this.x,
            y: this.y,
        })
        this.gradientArc.image[Config.namespace] = {type: 'gradientArc'}

        // Gloss effect overlay
        this.planetOverlay = Config.scene.add.image(
            this.x,
            this.y,
            'planetOverlay'
        )
        this.planetOverlay.setScale(this.diameter / 90)
        this.planetOverlay.setDepth(6)
        this.planetOverlay[Config.namespace] = {
            type: 'planet overlay sprite',
        }

        // Planet feature
        this.featureId = props.featureId || Phaser.Math.Between(1, 8)
        this.featureColor = props.featureColor || this.generateFeatureColor()

        this.planetFeature = Config.scene.add.image(
            this.x,
            this.y,
            `planetFeature${this.featureId}`
        )
        this.planetFeature.setScale(this.diameter / 76)
        this.planetFeature.setDepth(5)
        this.planetFeature[Config.namespace] = {
            type: 'planet feature',
        }

        // Selection overlay
        this.selectionGraphics = Config.scene.add.graphics()
        this.selectionGraphics.fillStyle('0xffffff')
        this.selectionGraphics.fillCircleShape(this.circle)
        this.selectionGraphics.setDepth(6)
        this.selectionGraphics[Config.namespace] = {
            type: 'selectionGraphics',
        }
    }

    // TODO: sort this out using config instead
    getFlagTexture() {
        const isEnemy = this.faction?.includes(Config.enemyFactionName)
        let flagTexture = 'flagGreen'

        if (isEnemy) {
            flagTexture =
                this.enemyFlagTextureMap[this.faction.at(-1)] || 'flagRed'
        }
        return flagTexture
    }

    generateFeatureColor() {
        // https://github.com/bgrins/TinyColor
        // https://bgrins.github.io/TinyColor/
        // https://rexrainbow.github.io/phaser3-rex-notes/docs/site/color/
        const tinyColorFill = tinycolor(`#${this.fillColor.replace('0x', '')}`)
        // const complement = tinyColorFill.complement().toHexString()
        // const complementColor = Phaser.Display.Color.ValueToColor(complement)

        const tetrad = tinyColorFill.tetrad()
        const colors = tetrad.map((t) => t.toHexString())
        const chosen = Config.chance.pick([colors[1], colors[2], colors[3]])
        let complementColor = Phaser.Display.Color.ValueToColor(chosen)

        if (this.climate === 'ice') {
            complementColor = Phaser.Display.Color.ValueToColor('#ffffff')
        }

        const {alpha, blue, green, red} = complementColor
        const hexString = Phaser.Display.Color.RGBToString(
            red,
            green,
            blue,
            alpha
        )
        // this.featureColorPhaser = complementColor
        // this.featureColorHex = hexString

        return hexString.replace('#', '0x')
    }

    deselect() {
        super.deselect()
    }

    destroy() {
        this.selectionGraphics.destroy()
        this.planetOverlay.destroy()
        this.planetFeature.destroy()
        this.gradientArc.image.destroy()

        super.destroy()
    }

    update() {
        super.update()

        if (this.selectionGraphics) {
            this.selectionGraphics.alpha =
                this.selected && (this.discovered || isDebug) ? 0.8 : 0
        }

        this.planetOverlay.setVisible(this.discovered || isDebug)
        this.gradientArc.image.setVisible(this.discovered || isDebug)

        this.flag.setVisible(this.colonized && (this.discovered || isDebug))

        this.flag.setTexture(this.getFlagTexture())

        this.resourcesSprite.setVisible(
            this.isMiningResources &&
                this.faction === Config.userFactionName &&
                this.colonized &&
                this.discovered
        )

        if (this.homeSprite) {
            this.homeSprite.setVisible(this.isHomePlanet)
        }

        this.planetFeature.setVisible(this.discovered || isDebug)
        this.planetFeature.setTint(
            this.selected && (this.discovered || isDebug)
                ? 0x222222
                : this.featureColor
        )
    }
}
